import React from 'react';
import { AppScenario } from '../data/mock-data';
import { getDistanceMeters, hasCoordinates } from '../lib/geo';
import { getPhotoStatusHelp, getPhotoStatusLabel } from '../lib/photoStatus';

interface DetailSheetProps {
  scenarioState: AppScenario;
  onSelectPhoto: (id: string) => void;
  selectedPhotoId: string | null;
  onRetry?: () => void;
  onDeletePhoto?: (id: string) => void;
  deletingPhotoId?: string | null;
  isFilteredEmpty?: boolean;
}

export const DetailSheet: React.FC<DetailSheetProps> = ({
  scenarioState,
  onSelectPhoto,
  selectedPhotoId,
  onRetry,
  onDeletePhoto,
  deletingPhotoId = null,
  isFilteredEmpty = false,
}) => {
  const formatCapturedAt = (value: string | null) => {
    if (!value) return '알 수 없음';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('ko-KR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const formatDistance = (value: number) => {
    if (value < 10) return '10m 이내';
    if (value < 1000) return `약 ${Math.round(value)}m`;

    return `약 ${(value / 1000).toFixed(1)}km`;
  };

  const getRecordLabel = (capturedAt: string | null) => (
    capturedAt ? formatCapturedAt(capturedAt) : '촬영시각 없음'
  );

  const renderHeading = (title: string) => (
    <div>
      <p className="eyebrow">기록</p>
      <h2 id="detail-heading">{title}</h2>
    </div>
  );

  if (scenarioState.detail.status === 'loading') {
    return (
      <aside className="detail-panel">
        <section className="panel">
          {renderHeading('공사 사진 기록')}
          <div className="detail-state" role="status">
            <strong>공사 사진 기록을 불러오고 있습니다</strong>
            <p>GPS, 촬영 시각, 압축 사진을 준비하는 중입니다.</p>
          </div>
        </section>
      </aside>
    );
  }

  if (scenarioState.detail.status === 'empty') {
    return (
      <aside className="detail-panel">
        <section className="panel">
          {renderHeading('선택한 기록 없음')}
          <div className="detail-state" role="status">
            <strong>선택한 기록이 없습니다</strong>
            <p>지도 썸네일이나 사진 목록을 선택하면 현장 기록이 표시됩니다.</p>
          </div>
        </section>
      </aside>
    );
  }

  if (scenarioState.detail.status === 'error') {
    return (
      <aside className="detail-panel">
        <section className="panel">
          {renderHeading('기록 불러오기 실패')}
          <div className="detail-state" role="alert">
            <strong>공사 사진 기록을 불러오지 못했습니다</strong>
            <p>연결 상태를 확인한 뒤 다시 불러오세요.</p>
            {onRetry && (
              <button className="button button--secondary" type="button" onClick={onRetry}>
                다시 불러오기
              </button>
            )}
          </div>
        </section>
      </aside>
    );
  }

  const selectedPhoto = scenarioState.photos.find((p) => p.id === selectedPhotoId) ?? scenarioState.photos[0];

  if (!selectedPhoto) {
    return (
      <aside className="detail-panel">
        <section className="panel">
          {renderHeading('공사 사진 없음')}
          <div className="detail-state" role="status">
            <strong>{isFilteredEmpty ? '선택한 기간에 사진이 없습니다' : '공사 사진이 없습니다'}</strong>
            <p>
              {isFilteredEmpty
                ? '기간을 초기화하거나 다른 기간을 선택하면 사진 기록을 다시 볼 수 있습니다.'
                : '현장 사진을 등록하면 위치와 상세 기록이 여기에 표시됩니다.'}
            </p>
          </div>
        </section>
      </aside>
    );
  }

  const capturedAtLabel = formatCapturedAt(selectedPhoto.capturedAt);
  const nearbyPhotos = hasCoordinates(selectedPhoto)
    ? scenarioState.photos
      .filter((photo) => photo.id !== selectedPhoto.id && hasCoordinates(photo))
      .map((photo) => ({
        photo,
        distanceM: getDistanceMeters(selectedPhoto, photo),
      }))
      .filter((item): item is { photo: AppScenario['photos'][number]; distanceM: number } => (
        item.distanceM != null && item.distanceM <= 50
      ))
      .sort((a, b) => a.distanceM - b.distanceM)
    : [];

  const renderStatusBadge = (status: string) => (
    <span className={`status-badge status-badge--${status}`}>
      {getPhotoStatusLabel(status)}
    </span>
  );

  const renderDetailStatus = (status: string) => (
    <span className={`detail-status-pill detail-status-pill--${status}`}>
      {getPhotoStatusLabel(status)}
    </span>
  );

  return (
    <aside className="detail-panel">
      <section className="panel">
        <article className="detail-sheet" aria-labelledby="detail-heading">
          {renderHeading(capturedAtLabel)}
          
          <div className="detail-preview">
            {selectedPhoto.previewUrl && (
              <img src={selectedPhoto.previewUrl} alt="선택한 공사 사진" />
            )}
            {!selectedPhoto.previewUrl && selectedPhoto.mediaError && (
              <div className="detail-preview__fallback" role="status">
                <strong>미리보기를 불러오지 못했습니다</strong>
                <span>사진 기록은 남아 있지만 비공개 미디어 주소를 만들지 못했습니다.</span>
              </div>
            )}
          </div>
          
          <div className="detail-summary">
            <strong>선택한 사진 기록</strong>
          </div>
          
          <div className="detail-actions">
            {selectedPhoto.previewUrl ? (
              <a className="button button--primary" href={selectedPhoto.previewUrl} target="_blank" rel="noreferrer">
                미리보기 열기
              </a>
            ) : (
              <button className="button button--primary" type="button" disabled>
                사진 없음
              </button>
            )}
            {renderDetailStatus(selectedPhoto.status)}
            {(selectedPhoto.status === 'missing_gps' || selectedPhoto.status === 'failed') && onDeletePhoto && (
              <button
                className="button button--secondary"
                type="button"
                disabled={deletingPhotoId === selectedPhoto.id}
                onClick={() => onDeletePhoto(selectedPhoto.id)}
              >
                {deletingPhotoId === selectedPhoto.id
                  ? '삭제 중'
                  : '사진 삭제'}
              </button>
            )}
          </div>

          {!hasCoordinates(selectedPhoto) ? (
            <div className="photo-list-empty" role="status">
              <strong>사진 위치가 없습니다</strong>
              <p>{getPhotoStatusHelp(selectedPhoto)}</p>
            </div>
          ) : nearbyPhotos.length > 0 ? (
            <>
              <p className="photo-list-heading">반경 50m 이내 사진</p>
              <div className="photo-strip" aria-label="반경 50m 이내 공사 사진">
                {nearbyPhotos.map(({ photo, distanceM }) => (
                  <article key={photo.id} className="photo-card">
                    {photo.previewUrl ? (
                      <img src={photo.previewUrl} alt="인접 공사 사진" />
                    ) : photo.mediaError ? (
                      <div className="photo-card__media-fallback">미리보기 실패</div>
                    ) : (
                      <div className="detail-preview"></div>
                    )}
                    <div className="photo-card__body">
                      <strong>{getRecordLabel(photo.capturedAt)}</strong>
                      {renderStatusBadge(photo.status)}
                      <span className="muted">선택 위치에서 {formatDistance(distanceM)}</span>
                      <button
                        className="button button--secondary"
                        type="button"
                        onClick={() => onSelectPhoto(photo.id)}
                      >
                        {photo.hasGps ? '지도에서 확인' : '기록 열기'}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="photo-list-empty" role="status">
              <strong>반경 50m 이내 사진이 없습니다</strong>
              <p>같은 위치의 다른 사진이 있으면 이 영역에 함께 표시됩니다.</p>
            </div>
          )}
        </article>
      </section>
    </aside>
  );
};
